import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';
const MAIN_API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      return idToken;
    }

    console.warn('Template API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Template API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT', 'DELETE').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @param {string} [apiName='Template'] - API name for logging (default: 'Template')
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null, apiName = 'Template') => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `${apiName} API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    
    let response;
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        const corsError = new Error(
          `CORS Error: Unable to connect to the ${apiName} API. ` +
          'This may be a preflight (OPTIONS) request issue or backend CORS configuration issue. ' +
          'The API Gateway needs to be configured to allow CORS from your origin.'
        );
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true;
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error(`${apiName} API: CORS/Preflight Error Details:`, {
          method,
          endpoint,
          url: `${API_BASE_URL}${endpoint}`,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers (Access-Control-Allow-Origin, Access-Control-Allow-Methods, Access-Control-Allow-Headers)',
        });
        
        // Log CORS errors to logging service
        loggingService.logError(
          'api_error_template',
          `${apiName} API CORS/network error for ${endpoint}`,
          corsError,
          {
            endpoint,
            method,
            errorType: 'CORS_ERROR',
            apiName,
          }
        ).catch(logError => {
          console.warn(`[${apiName}Api] Failed to log error:`, logError);
        });
        
        throw corsError;
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `${apiName} API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_template',
        `${apiName} API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
          apiName,
        }
      ).catch(logError => {
        console.warn(`[${apiName}Api] Failed to log error:`, logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    // Re-throw CORS errors as-is
    if (error.isCorsError) {
      throw error;
    }

    console.error(
      `${apiName} API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_template',
      `${apiName} API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
        apiName,
      }
    ).catch(logError => {
      console.warn(`[${apiName}Api] Failed to log error:`, logError);
    });
    
    throw error;
  }
};

/**
 * Shared helper: Fetches available sites.
 * GET /site/getall
 * Note: Sites endpoint is on the main API, not the Template API
 * @returns {Promise<Array>} Array of site objects
 */
const getSites = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(
      `Template API: Calling endpoint: ${MAIN_API_BASE_URL}/site/getall`
    );

    const response = await fetch(`${MAIN_API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(
        `Template API: HTTP Error: ${response.status} ${response.statusText}`
      );
      console.log(`Template API: Response Body:`, responseText);

      let errorMessage;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Template API: Sites response:', result);

    // Handle different response formats
    if (Array.isArray(result)) {
      return result;
    }
    return result.sites || result.data || [];
  } catch (error) {
    console.error('Template API: Error in getSites:', error);
    throw error;
  }
};

/**
 * API utility functions for Product Template Management.
 * Product Templates are for finished goods/products (not production recipes).
 */
export const productTemplateApi = {
  /**
   * Fetches available sites.
   * GET /site/getall
   * Note: Sites endpoint is on the main API, not the Product Template API
   * @returns {Promise<Array>} Array of site objects
   */
  getSites,

  /**
   * Creates a product template.
   * POST /stock/products/templates
   * @param {Object} templateData - Product template data
   * @param {string} templateData.template_name - Template name (required, also accepts `product_name` or `name` as aliases)
   * @param {Array} [templateData.bom] - Bill of Materials array (API may require non-empty list)
   * @param {string} [templateData.bom[].raw_material_id] - Raw material ID (required if production_template_id not provided)
   * @param {string} [templateData.bom[].production_template_id] - Production template ID (required if raw_material_id not provided)
   * @param {boolean} [templateData.is_internal_use=false] - Internal use only flag (optional, default: false)
   * @param {string} [templateData.category] - Product category
   * @param {string} [templateData.sku] - Stock Keeping Unit identifier
   * @param {number} [templateData.price] - Selling price (allows zero)
   * @param {number} [templateData.cost] - Cost price (allows zero)
   * @param {string} [templateData.unit] - Unit of measurement (e.g., "pcs", "kg", "liters")
   * @param {string} [templateData.barcode] - Barcode/EAN/UPC identifier
   * @param {string} [templateData.description] - Product description
   * @param {string} [templateData.photo_key] - S3 key for product image
   * @param {string} [templateData.status] - Product status (default: "active")
   * @param {string} [templateData.site_id] - Associated site ID
   * @param {string} [templateData.site_name] - Site name for denormalization
   * @param {Object} [templateData.metadata] - Arbitrary JSON metadata
   * @returns {Promise<Object>} Object containing success status and template data
   * @note The API may require a non-empty `bom` array. If you encounter "bom must be a non-empty list" error,
   *       you may need to provide a BOM array with at least one entry, or check with the backend team
   *       if BOM should be optional for simple product templates.
   */
  createTemplate: async templateData => {
    try {
      // API expects 'template_name' field
      // Support 'product_name', 'name', and 'template_name' as aliases
      if (templateData.product_name && !templateData.template_name) {
        templateData.template_name = templateData.product_name;
      } else if (templateData.name && !templateData.template_name) {
        templateData.template_name = templateData.name;
      }
      
      // Remove aliases to avoid confusion
      if (templateData.product_name && templateData.template_name) {
        delete templateData.product_name;
      }
      if (templateData.name && templateData.template_name) {
        delete templateData.name;
      }

      // Default is_internal_use to false if not provided
      if (templateData.is_internal_use === undefined) {
        templateData.is_internal_use = false;
      }
      
      // API requires bom to be a non-empty list
      // Validate and ensure BOM is properly formatted
      if (templateData.bom !== undefined) {
        if (!Array.isArray(templateData.bom)) {
          throw new Error('BOM must be an array');
        }
        if (templateData.bom.length === 0) {
          throw new Error('BOM must be a non-empty list (at least one item required)');
        }
        // Validate each BOM entry
        templateData.bom.forEach((entry, index) => {
          const hasRawMaterial = entry.raw_material_id && entry.raw_material_id.trim() !== '';
          const hasProductionTemplate = entry.production_template_id && entry.production_template_id.trim() !== '';
          const hasItemId = entry.item_id && entry.item_id.trim() !== '';

          if (!hasRawMaterial && !hasProductionTemplate && !hasItemId) {
            throw new Error(`BOM entry ${index + 1}: Either raw_material_id or production_template_id is required`);
          }

          if ((hasRawMaterial || hasItemId) && hasProductionTemplate) {
            throw new Error(`BOM entry ${index + 1}: Cannot have both raw_material_id and production_template_id. Use only one.`);
          }

          if (!entry.quantity || parseFloat(entry.quantity) <= 0) {
            throw new Error(`BOM entry ${index + 1}: quantity is required and must be greater than 0`);
          }
          if (!entry.unit || entry.unit.trim() === '') {
            throw new Error(`BOM entry ${index + 1}: unit is required`);
          }
        });
        // Support 'item_id' alias in BOM entries
        templateData.bom = templateData.bom.map(bomEntry => {
          if (bomEntry.item_id && !bomEntry.raw_material_id) {
            bomEntry.raw_material_id = bomEntry.item_id;
            delete bomEntry.item_id;
          }
          return bomEntry;
        });
      } else {
        // BOM is required for product templates
        throw new Error('BOM field is required and must be a non-empty list');
      }

      console.log(
        'Product Template API: Creating template with payload:',
        JSON.stringify(templateData, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/products/templates',
        'POST',
        templateData,
        'Product Template'
      );
      const responseData = await response.json();

      console.log('Product Template API: Create template response:', responseData);
      console.log('Product Template API: Response keys:', Object.keys(responseData));
      console.log('Product Template API: Template object:', responseData.template);
      console.log('Product Template API: Data object:', responseData.data);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data || responseData,
        // Also include the full response for easier ID extraction
        response: responseData,
      };
    } catch (error) {
      console.error('Product Template API: Error in createTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      // Handle CORS errors specifically
      if (error.isCorsError || error.isPreflightIssue) {
        return {
          success: false,
          error: 'CORS Error',
          message:
            'CORS configuration issue: The Product Template API Gateway needs to be configured to allow CORS from your origin. ' +
            'Please contact the backend team to enable CORS for the /stock/products/templates endpoint.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to create product template',
      };
    }
  },

  /**
   * Lists product templates with optional filters.
   * GET /stock/products/templates
   * @param {Object} params - Optional parameters
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.lastKey] - JSON-encoded DynamoDB key for pagination
   * @param {string} [params.nameContains] - Case-insensitive substring filter on product name
   * @param {string} [params.category] - Exact category match
   * @param {string} [params.status] - Status filter (active, archived, etc.)
   * @param {string} [params.siteId] - Filter by site ID
   * @param {boolean} [params.isInternalUse] - Filter by internal use flag (true = internal use only, false = sellable only, undefined = all)
   * @returns {Promise<Object>} Object containing success status and templates array
   */
  listTemplates: async ({
    limit,
    lastKey,
    nameContains,
    category,
    status,
    siteId,
    isInternalUse,
  } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (limit) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey) {
        queryParams.append('last_key', lastKey);
      }
      if (nameContains) {
        queryParams.append('name_contains', nameContains);
      }
      if (category) {
        queryParams.append('category', category);
      }
      if (status) {
        queryParams.append('status', status);
      }
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (isInternalUse !== undefined) {
        queryParams.append('is_internal_use', isInternalUse.toString());
      }

      const endpoint = `/stock/products/templates${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      const response = await makeApiRequest(endpoint, 'GET', null, 'Product Template');
      const responseData = await response.json();

      console.log('Product Template API: List templates response:', responseData);

      return {
        success: true,
        data: responseData,
        templates: responseData.templates || [],
        count: responseData.count || 0,
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Product Template API: Error in listTemplates:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          templates: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch product templates',
        templates: [],
      };
    }
  },

  /**
   * Gets a single product template by ID.
   * GET /stock/production/templates/{id}
   * @param {string} templateId - Product template ID
   * @returns {Promise<Object>} Object containing success status and template data
   */
  getTemplate: async templateId => {
    try {
      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'GET',
        null,
        'Product Template'
      );
      const responseData = await response.json();

      console.log('Product Template API: Get template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Product Template API: Error in getTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch product template',
      };
    }
  },

  /**
   * Updates a product template (partial updates supported).
   * PUT /stock/production/templates/{id}
   * @param {string} templateId - Product template ID
   * @param {Object} updateData - Data to update (all fields optional)
   * @param {string} [updateData.product_name] - Product name (also accepts `name` as alias)
   * @param {string} [updateData.category] - Product category
   * @param {string} [updateData.sku] - Stock Keeping Unit identifier
   * @param {number} [updateData.price] - Selling price (allows zero)
   * @param {number} [updateData.cost] - Cost price (allows zero)
   * @param {string} [updateData.unit] - Unit of measurement
   * @param {string} [updateData.barcode] - Barcode/EAN/UPC identifier
   * @param {string} [updateData.status] - Product status
   * @param {string} [updateData.description] - Product description
   * @param {string} [updateData.photo_key] - S3 key for product image
   * @param {boolean} [updateData.is_internal_use] - Internal use only flag
   * @param {Object} [updateData.metadata] - Arbitrary JSON metadata
   * @returns {Promise<Object>} Object containing success status
   */
  updateTemplate: async (templateId, updateData) => {
    try {
      // Support both 'product_name' and 'name' as aliases
      if (updateData.name && !updateData.product_name) {
        updateData.product_name = updateData.name;
        delete updateData.name;
      }

      console.log(
        `Product Template API: Updating template ${templateId} with data:`,
        updateData
      );

      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'PUT',
        updateData,
        'Product Template'
      );
      const responseData = await response.json();

      console.log('Product Template API: Update template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Product Template API: Error in updateTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to update product template',
      };
    }
  },

  /**
   * Archives a product template (soft delete).
   * DELETE /stock/production/templates/{id}
   * @param {string} templateId - Product template ID
   * @returns {Promise<Object>} Object containing success status
   */
  archiveTemplate: async templateId => {
    try {
      console.log(
        `Product Template API: Archiving template ${templateId}`
      );

      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'DELETE',
        null,
        'Product Template'
      );
      const responseData = await response.json();

      console.log('Product Template API: Archive template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Product Template API: Error in archiveTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to archive product template',
      };
    }
  },

  /**
   * Gets a presigned URL for photo upload.
   * POST /stock/production/templates/{id}/presign
   * Note: Uses production templates endpoint as products templates presign endpoint doesn't exist
   * (API Gateway resource limit). Both use the same backend Lambda and data structure.
   * @param {string} templateId - Product template ID
   * @param {string} [contentType='image/jpeg'] - Image content type
   * @returns {Promise<Object>} Object containing presigned URL data
   */
  presignPhotoUpload: async (templateId, contentType = 'image/jpeg') => {
    try {
      const queryParams = new URLSearchParams();
      if (contentType) {
        queryParams.append('content_type', contentType);
      }

      // Use production templates endpoint (same backend, CORS configured)
      const endpoint = `/stock/production/templates/${templateId}/presign${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      console.log(
        'Product Template API: Getting presigned URL for template:',
        templateId,
        '(using production templates endpoint)'
      );

      const response = await makeApiRequest(endpoint, 'POST', null, 'Product Template');
      const responseData = await response.json();

      console.log(
        'Product Template API: Presigned URL response:',
        responseData
      );

      return {
        success: true,
        data: responseData,
        postData: responseData.post_data || responseData.postData,
        s3Uri: responseData.s3_uri || responseData.s3Uri,
        photoKey: responseData.photo_key || responseData.photoKey,
        templateId: responseData.template_id || templateId,
      };
    } catch (error) {
      console.error(
        'Product Template API: Error in presignPhotoUpload:',
        error
      );

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to get presigned URL',
      };
    }
  },

  /**
   * Uploads a photo to S3 using presigned POST data.
   * @param {Object} presignResult - Result from presignPhotoUpload
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<Object>} Object containing upload success status
   */
  uploadPhotoToS3: async (presignResult, imageFile) => {
    try {
      const postData =
        presignResult.postData ||
        presignResult.data?.post_data ||
        presignResult.data;

      if (!postData || !postData.url || !postData.fields) {
        throw new Error('Invalid presigned URL response: missing post_data');
      }

      console.log('Product Template API: Uploading to S3:', {
        url: postData.url,
        fields: Object.keys(postData.fields),
      });

      // Create FormData for multipart/form-data upload
      const formData = new FormData();

      // Add all fields from presigned POST
      Object.keys(postData.fields).forEach(key => {
        formData.append(key, postData.fields[key]);
      });

      // Add the file - try different field names
      const fileFieldName =
        postData.file_field || postData.fields.file_field || 'file';
      formData.append(fileFieldName, imageFile);

      // Upload to S3
      const uploadResponse = await fetch(postData.url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error(
          `Product Template API: S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          errorText
        );
        throw new Error(
          `S3 upload failed: ${uploadResponse.status} - ${errorText}`
        );
      }

      console.log(
        '✅ Product Template API: Image uploaded to S3 successfully'
      );

      // Extract photo_key from presigned POST fields or S3 URI
      const photoKey =
        presignResult.photoKey ||
        presignResult.data?.photo_key ||
        postData.fields?.key ||
        presignResult.s3Uri?.replace(/^s3:\/\/[^/]+\//, '') ||
        presignResult.s3Uri?.replace(/^https?:\/\/[^/]+\//, '');

      return {
        success: true,
        s3Uri: presignResult.s3Uri || presignResult.data?.s3_uri,
        photoKey: photoKey,
      };
    } catch (error) {
      console.error('Product Template API: Error in uploadPhotoToS3:', error);

      // Check for CORS errors
      if (
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control')
      ) {
        return {
          success: false,
          error: 'CORS Error',
          message:
            'Image upload failed due to CORS configuration. Please check S3 bucket CORS settings.',
        };
      }

      return {
        success: false,
        error: 'Upload Error',
        message: error.message || 'Failed to upload image to S3',
      };
    }
  },
};

/**
 * API utility functions for Production Template Management.
 * Production Templates define Bill of Materials (BOM) for products.
 */
export const productionTemplateApi = {
  /**
   * Fetches available sites.
   * GET /site/getall
   * @returns {Promise<Array>} Array of site objects
   */
  getSites,

  /**
   * Fetches stock items for a site.
   * GET /stock/items
   * @param {Object} params - Optional parameters
   * @param {string} [params.siteId] - Filter by site ID
   * @param {number} [params.limit] - Maximum items to return
   * @returns {Promise<Object>} Object containing success status and items array
   */
  getStockItems: async ({ siteId, limit } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (limit) {
        queryParams.append('limit', limit.toString());
      }

      const endpoint = `/stock/items${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      const response = await makeApiRequest(endpoint, 'GET', null, 'Production Template');
      const responseData = await response.json();

      console.log('Production Template API: Get stock items response:', responseData);

      return {
        success: true,
        data: responseData,
        items: responseData.items || [],
      };
    } catch (error) {
      console.error('Production Template API: Error in getStockItems:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          items: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch stock items',
        items: [],
      };
    }
  },

  /**
   * Creates a production template.
   * POST /stock/production/templates
   * @param {Object} templateData - Template data
   * @param {string} templateData.template_name - Template name (required, also accepts `name` as alias)
   * @param {Array} templateData.bom - Bill of Materials array (required, non-empty)
   * @param {string} [bom[].raw_material_id] - Raw material ID (required if production_template_id not provided, also accepts `item_id` as alias)
   * @param {string} [bom[].production_template_id] - Production template ID (required if raw_material_id not provided)
   * @param {number} bom[].quantity - Quantity (required, must be > 0)
   * @param {string} bom[].unit - Unit of measure (required)
   * @param {any} [bom[].specs] - Additional specifications (optional)
   * @param {boolean} [templateData.is_internal_use=false] - Internal use only flag (optional, default: false)
   * @param {number} [templateData.credits] - Credits/cost (optional, allows zero)
   * @param {number} [templateData.labour_cost] - Labour cost (optional, allows zero)
   * @param {number} [templateData.raw_material_cost] - Total raw material cost (optional, allows zero)
   * @param {string} [templateData.category] - Template category (optional)
   * @param {string} [templateData.version] - Version identifier (optional)
   * @param {string} [templateData.description] - Description text (optional)
   * @param {string} [templateData.photo_key] - S3 key for template photo (optional)
   * @param {string} [templateData.status] - Template status (optional, default: "active")
   * @param {Object} [templateData.metadata] - Arbitrary JSON metadata (optional)
   * @param {string} [templateData.production_template_id] - Custom ID (optional, auto-generated if not provided)
   * @param {boolean} [templateData.add_to_menu] - Add this to menu checkbox (optional)
   * @param {number} [templateData.calories_per_serve] - Computed as totalCalories / per_serve (optional)
   * @param {Object} [templateData.sop] - SOP object: { procedures: [{ sop_no?, heading, points }], estimated_calories } (optional)
   * @param {Array<string>} [templateData.allergens] - Allergen names e.g. ["Gluten", "Dairy"] (optional)
   * @param {Object} [templateData.allergen_data] - Structured allergen data { categorized, list } (optional)
   * @returns {Promise<Object>} Object containing success status and template data
   */
  createTemplate: async templateData => {
    try {
      // Support both 'template_name' and 'name' as aliases
      if (templateData.name && !templateData.template_name) {
        templateData.template_name = templateData.name;
        delete templateData.name;
      }

      // Default is_internal_use to false if not provided
      if (templateData.is_internal_use === undefined) {
        templateData.is_internal_use = false;
      }

      // Support 'item_id' alias in BOM entries and validate BOM structure
      if (templateData.bom && Array.isArray(templateData.bom)) {
        templateData.bom = templateData.bom.map((bomEntry, index) => {
          // Support 'item_id' alias
          if (bomEntry.item_id && !bomEntry.raw_material_id) {
            bomEntry.raw_material_id = bomEntry.item_id;
            delete bomEntry.item_id;
          }

          // Validate that exactly one of raw_material_id or production_template_id is provided
          const hasRawMaterial = bomEntry.raw_material_id && bomEntry.raw_material_id.trim() !== '';
          const hasProductionTemplate = bomEntry.production_template_id && bomEntry.production_template_id.trim() !== '';

          if (!hasRawMaterial && !hasProductionTemplate) {
            throw new Error(`BOM entry ${index + 1}: Either raw_material_id or production_template_id is required`);
          }

          if (hasRawMaterial && hasProductionTemplate) {
            throw new Error(`BOM entry ${index + 1}: Cannot have both raw_material_id and production_template_id. Use only one.`);
          }

          return bomEntry;
        });
      }

      console.log(
        'Production Template API: Creating template with payload:',
        JSON.stringify(templateData, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/production/templates',
        'POST',
        templateData,
        'Production Template'
      );
      const responseData = await response.json();

      console.log('Production Template API: Create template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Production Template API: Error in createTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to create template',
      };
    }
  },

  /**
   * Lists production templates.
   * GET /stock/production/templates
   * @param {Object} params - Optional parameters
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.lastKey] - JSON-encoded DynamoDB key for pagination
   * @param {string} [params.nameContains] - Case-insensitive substring filter on template name
   * @param {string} [params.category] - Exact category match (uses tenant_id-category-index GSI)
   * @param {string} [params.status] - Status filter (active, archived, etc.)
   * @param {boolean} [params.isInternalUse] - Filter by internal use flag (true = internal use only, false = sellable only, undefined = all)
   * @returns {Promise<Object>} Object containing success status and templates array
   */
  listTemplates: async ({
    limit,
    lastKey,
    nameContains,
    category,
    status,
    isInternalUse,
  } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (limit) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey) {
        queryParams.append('last_key', lastKey);
      }
      if (nameContains) {
        queryParams.append('name_contains', nameContains);
      }
      if (category) {
        queryParams.append('category', category);
      }
      if (status) {
        queryParams.append('status', status);
      }
      if (isInternalUse !== undefined) {
        queryParams.append('is_internal_use', isInternalUse.toString());
      }

      const endpoint = `/stock/production/templates${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      const response = await makeApiRequest(endpoint, 'GET', null, 'Production Template');
      const responseData = await response.json();

      console.log('Production Template API: List templates response:', responseData);

      return {
        success: true,
        data: responseData,
        templates: responseData.templates || [],
        count: responseData.count || 0,
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Production Template API: Error in listTemplates:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          templates: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch templates',
        templates: [],
      };
    }
  },

  /**
   * Gets a single production template by ID.
   * GET /stock/production/templates/{id}
   * @param {string} templateId - Production template ID
   * @returns {Promise<Object>} Object containing success status and template data
   */
  getTemplate: async templateId => {
    try {
      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'GET',
        null,
        'Production Template'
      );
      const responseData = await response.json();

      console.log('Production Template API: Get template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Production Template API: Error in getTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch production template',
      };
    }
  },

  /**
   * Gets a presigned URL for photo upload.
   * POST /stock/production/templates/{id}/presign
   * @param {string} templateId - Template ID (must exist)
   * @param {string} [contentType='image/jpeg'] - MIME type (default: "image/jpeg")
   * @returns {Promise<Object>} Object containing presigned URL data
   */
  presignPhotoUpload: async (templateId, contentType = 'image/jpeg') => {
    try {
      const queryParams = new URLSearchParams();
      if (contentType) {
        queryParams.append('content_type', contentType);
      }

      const endpoint = `/stock/production/templates/${templateId}/presign${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      console.log(
        'Production Template API: Getting presigned URL for template:',
        templateId
      );

      const response = await makeApiRequest(endpoint, 'POST', null, 'Production Template');
      const responseData = await response.json();

      console.log(
        'Production Template API: Presigned URL response:',
        responseData
      );

      return {
        success: true,
        data: responseData,
        postData: responseData.post_data || responseData.postData,
        s3Uri: responseData.s3_uri || responseData.s3Uri,
        photoKey: responseData.photo_key || responseData.photoKey,
      };
    } catch (error) {
      console.error(
        'Production Template API: Error in presignPhotoUpload:',
        error
      );

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to get presigned URL',
      };
    }
  },

  /**
   * Uploads a photo to S3 using presigned POST data.
   * @param {Object} presignResult - Result from presignPhotoUpload
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<Object>} Object containing upload success status
   */
  uploadPhotoToS3: async (presignResult, imageFile) => {
    try {
      const postData =
        presignResult.postData ||
        presignResult.data?.post_data ||
        presignResult.data;

      if (!postData || !postData.url || !postData.fields) {
        throw new Error('Invalid presigned URL response: missing post_data');
      }

      console.log('Production Template API: Uploading to S3:', {
        url: postData.url,
        fields: Object.keys(postData.fields),
      });

      // Create FormData for multipart/form-data upload
      const formData = new FormData();

      // Add all fields from presigned POST
      Object.keys(postData.fields).forEach(key => {
        formData.append(key, postData.fields[key]);
      });

      // Add the file - try different field names
      const fileFieldName =
        postData.file_field || postData.fields.file_field || 'file';
      formData.append(fileFieldName, imageFile);

      // Upload to S3
      const uploadResponse = await fetch(postData.url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error(
          `Production Template API: S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          errorText
        );
        throw new Error(
          `S3 upload failed: ${uploadResponse.status} - ${errorText}`
        );
      }

      console.log(
        '✅ Production Template API: Image uploaded to S3 successfully'
      );

      return {
        success: true,
        s3Uri: presignResult.s3Uri || presignResult.data?.s3_uri,
        photoKey: presignResult.photoKey || presignResult.data?.photo_key,
      };
    } catch (error) {
      console.error('Production Template API: Error in uploadPhotoToS3:', error);

      // Check for CORS errors
      if (
        error.message?.includes('CORS') ||
        error.message?.includes('Access-Control')
      ) {
        return {
          success: false,
          error: 'CORS Error',
          message:
            'Image upload failed due to CORS configuration. Please check S3 bucket CORS settings.',
        };
      }

      return {
        success: false,
        error: 'Upload Error',
        message: error.message || 'Failed to upload image to S3',
      };
    }
  },

  /**
   * Updates a production template (partial updates supported).
   * PUT /stock/production/templates/{id}
   * @param {string} templateId - Production template ID
   * @param {Object} updateData - Data to update (all fields optional)
   * @param {string} [updateData.template_name] - Template name (also accepts `name` as alias)
   * @param {string} [updateData.category] - Template category
   * @param {string} [updateData.version] - Version identifier
   * @param {string} [updateData.status] - Template status
   * @param {string} [updateData.description] - Description text
   * @param {string} [updateData.photo_key] - S3 key for template photo
   * @param {boolean} [updateData.is_internal_use] - Internal use only flag
   * @param {number} [updateData.credits] - Credits/cost (allows zero)
   * @param {number} [updateData.labour_cost] - Labour cost (allows zero)
   * @param {number} [updateData.raw_material_cost] - Total raw material cost (allows zero)
   * @param {Array} [updateData.bom] - Bill of Materials (completely replaces existing BOM when provided)
   * @param {string} [updateData.bom[].raw_material_id] - Raw material ID (required if production_template_id not provided)
   * @param {string} [updateData.bom[].production_template_id] - Production template ID (required if raw_material_id not provided)
   * @param {Object} [updateData.metadata] - Arbitrary JSON metadata
   * @param {boolean} [updateData.add_to_menu] - Add this to menu (optional)
   * @param {number} [updateData.calories_per_serve] - Calories per serve (optional)
   * @param {Object} [updateData.sop] - SOP { procedures, estimated_calories } (optional)
   * @param {Array<string>} [updateData.allergens] - Allergen names (optional)
   * @param {Object} [updateData.allergen_data] - Structured allergen data (optional)
   * @returns {Promise<Object>} Object containing success status
   */
  updateTemplate: async (templateId, updateData) => {
    try {
      // Support both 'template_name' and 'name' as aliases
      if (updateData.name && !updateData.template_name) {
        updateData.template_name = updateData.name;
        delete updateData.name;
      }

      // Support 'item_id' alias in BOM entries and validate BOM structure if BOM is being updated
      if (updateData.bom && Array.isArray(updateData.bom)) {
        updateData.bom = updateData.bom.map((bomEntry, index) => {
          // Support 'item_id' alias
          if (bomEntry.item_id && !bomEntry.raw_material_id) {
            bomEntry.raw_material_id = bomEntry.item_id;
            delete bomEntry.item_id;
          }

          // Validate that exactly one of raw_material_id or production_template_id is provided
          const hasRawMaterial = bomEntry.raw_material_id && bomEntry.raw_material_id.trim() !== '';
          const hasProductionTemplate = bomEntry.production_template_id && bomEntry.production_template_id.trim() !== '';

          if (!hasRawMaterial && !hasProductionTemplate) {
            throw new Error(`BOM entry ${index + 1}: Either raw_material_id or production_template_id is required`);
          }

          if (hasRawMaterial && hasProductionTemplate) {
            throw new Error(`BOM entry ${index + 1}: Cannot have both raw_material_id and production_template_id. Use only one.`);
          }

          return bomEntry;
        });
      }

      console.log(
        `Production Template API: Updating template ${templateId} with data:`,
        updateData
      );

      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'PUT',
        updateData,
        'Production Template'
      );
      const responseData = await response.json();

      console.log('Production Template API: Update template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Production Template API: Error in updateTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to update template',
      };
    }
  },

  /**
   * Archives a production template (soft delete).
   * DELETE /stock/production/templates/{id}
   * @param {string} templateId - Production template ID
   * @returns {Promise<Object>} Object containing success status
   */
  archiveTemplate: async templateId => {
    try {
      console.log(
        `Production Template API: Archiving template ${templateId}`
      );

      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'DELETE',
        null,
        'Production Template'
      );
      const responseData = await response.json();

      console.log('Production Template API: Archive template response:', responseData);

      return {
        success: true,
        data: responseData,
        template: responseData.template || responseData.data,
      };
    } catch (error) {
      console.error('Production Template API: Error in archiveTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to archive production template',
      };
    }
  },

  /**
   * Calculates nutrition from BOM data without requiring a template.
   * POST /stock/production/templates/nutrition/calculate
   * @param {Object} params - Nutrition calculation parameters
   * @param {Array} params.bom - Bill of Materials array (required)
   * @param {string} [params.bom[].raw_material_id] - Stock item ID or ingredient name (required if production_template_id not provided)
   * @param {string} [params.bom[].production_template_id] - Production template ID (required if raw_material_id not provided)
   * @param {number} params.bom[].quantity - Quantity value (required)
   * @param {string} params.bom[].unit - Unit of measure (grams, kg, pcs, etc.) (required)
   * @param {boolean} [params.recalculate=false] - Force recalculation, bypass cache (optional, default: false)
   * @returns {Promise<Object>} Object containing success status and nutrition data
   * @example
   * const result = await productionTemplateApi.calculateNutritionFromBOM({
   *   bom: [
   *     { raw_material_id: "bread-loaf", quantity: 2, unit: "pcs" },
   *     { production_template_id: "pizza-dough-base-id", quantity: 1, unit: "pcs" }
   *   ],
   *   recalculate: false
   * });
   */
  calculateNutritionFromBOM: async ({ bom, recalculate = false }) => {
    try {
      // Validate BOM array
      if (!bom || !Array.isArray(bom)) {
        throw new Error('BOM must be a non-empty array');
      }

      if (bom.length === 0) {
        throw new Error('BOM array cannot be empty');
      }

      // Validate each BOM entry
      bom.forEach((entry, index) => {
        const hasRawMaterial = entry.raw_material_id && entry.raw_material_id.trim() !== '';
        const hasProductionTemplate = entry.production_template_id && entry.production_template_id.trim() !== '';

        if (!hasRawMaterial && !hasProductionTemplate) {
          throw new Error(
            `BOM entry ${index + 1}: Either raw_material_id or production_template_id is required`
          );
        }

        if (hasRawMaterial && hasProductionTemplate) {
          throw new Error(
            `BOM entry ${index + 1}: Cannot have both raw_material_id and production_template_id. Use only one.`
          );
        }

        if (
          entry.quantity === undefined ||
          entry.quantity === null ||
          parseFloat(entry.quantity) <= 0
        ) {
          throw new Error(
            `BOM entry ${index + 1}: quantity is required and must be greater than 0`
          );
        }
        if (!entry.unit || entry.unit.trim() === '') {
          throw new Error(`BOM entry ${index + 1}: unit is required`);
        }
      });

      // Support 'item_id' alias in BOM entries
      const normalizedBom = bom.map(bomEntry => {
        if (bomEntry.item_id && !bomEntry.raw_material_id) {
          bomEntry.raw_material_id = bomEntry.item_id;
          delete bomEntry.item_id;
        }
        return bomEntry;
      });

      const requestBody = {
        bom: normalizedBom,
        recalculate: recalculate,
      };

      console.log(
        'Production Template API: Calculating nutrition from BOM:',
        JSON.stringify(requestBody, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/production/templates/nutrition/calculate',
        'POST',
        requestBody,
        'Production Template Nutrition'
      );
      const responseData = await response.json();

      console.log(
        'Production Template API: Calculate nutrition response:',
        responseData
      );

      return {
        success: true,
        data: responseData,
        totalCalories: responseData.total_calories,
        nutritionBreakdown: responseData.nutrition_breakdown,
        allergens: responseData.allergens,
        calculationStatus: responseData.calculation_status,
        calculatedAt: responseData.calculated_at,
        apiUsed: responseData.api_used,
      };
    } catch (error) {
      console.error(
        'Production Template API: Error in calculateNutritionFromBOM:',
        error
      );

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      // Handle validation errors
      if (error.message && error.message.includes('BOM entry')) {
        return {
          success: false,
          error: 'Validation Error',
          message: error.message,
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to calculate nutrition from BOM',
      };
    }
  },

  /**
   * Detects allergens from ingredient list using LLM (AI-powered).
   * POST /stock/production/templates/allergens/detect
   * Uses TinyLlama 1.1B model for AI-powered allergen detection.
   * 
   * @param {Object} params - Allergen detection parameters
   * @param {string[]} params.ingredients - Array of ingredient names to analyze (required, non-empty)
   * @returns {Promise<Object>} Object containing success status and allergen detection data
   * @example
   * const result = await productionTemplateApi.detectAllergensLLM({
   *   ingredients: [
   *     "wheat flour",
   *     "buffalo milk",
   *     "eggs",
   *     "almonds",
   *     "soy sauce"
   *   ]
   * });
   * 
   * // Response structure:
   * // {
   * //   success: true,
   * //   allergens: {
   * //     categorized: {
   * //       gluten: ["wheat flour"],
   * //       dairy: ["buffalo milk"],
   * //       eggs: ["eggs"],
   * //       nuts: ["almonds"],
   * //       soy: ["soy sauce"]
   * //     },
   * //     list: [
   * //       { name: "wheat flour", category: "gluten" },
   * //       { name: "buffalo milk", category: "dairy" },
   * //       ...
   * //     ]
   * //   },
   * //   detectedAt: "2024-01-15T10:30:00Z"
   * // }
   */
  detectAllergensLLM: async ({ ingredients }) => {
    try {
      // Validate ingredients array
      if (!ingredients || !Array.isArray(ingredients)) {
        throw new Error('ingredients must be a non-empty array');
      }

      if (ingredients.length === 0) {
        throw new Error('ingredients array cannot be empty');
      }

      // Validate each ingredient is a non-empty string
      ingredients.forEach((ingredient, index) => {
        if (typeof ingredient !== 'string' || ingredient.trim() === '') {
          throw new Error(
            `Ingredient ${index + 1} must be a non-empty string`
          );
        }
      });

      const requestBody = {
        ingredients: ingredients.map(ing => ing.trim()),
      };

      console.log(
        'Production Template API: Detecting allergens with LLM:',
        JSON.stringify(requestBody, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/production/templates/allergens/detect',
        'POST',
        requestBody,
        'Production Template Allergen Detection'
      );
      const responseData = await response.json();

      console.log(
        'Production Template API: LLM allergen detection response:',
        responseData
      );

      return {
        success: true,
        data: responseData,
        allergens: responseData.allergens,
        detectedAt: responseData.detected_at,
      };
    } catch (error) {
      console.error(
        'Production Template API: Error in detectAllergensLLM:',
        error
      );

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      // Handle validation errors
      if (error.message && (error.message.includes('ingredients') || error.message.includes('Ingredient'))) {
        return {
          success: false,
          error: 'Validation Error',
          message: error.message,
        };
      }

      // Handle specific API errors
      if (error.status === 400) {
        return {
          success: false,
          error: 'Bad Request',
          message:
            error.responseData?.error ||
            error.responseData?.message ||
            'Invalid ingredients provided',
        };
      }

      if (error.status === 504) {
        return {
          success: false,
          error: 'Timeout',
          message:
            error.responseData?.error ||
            'LLM inference timed out. Please try again.',
        };
      }

      if (error.status === 503) {
        return {
          success: false,
          error: 'Service Unavailable',
          message:
            error.responseData?.error ||
            'Model loading failed. Please try again later.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to detect allergens with LLM',
      };
    }
  },
};

